import React, { useCallback, useEffect, useRef, useState } from 'react';
import Zana, { ZanaState } from './Zana';
import { TakeoverState, TakeoverController } from '../services/zanaTakeover';
import { labelForZanaIntent } from '../services/zanaActions';

/* ============================================================================
   ZanaTakeoverPanel — Zana's cockpit while she is driving the console.

   Deliberately NOT the dock. The dock is Ask (questions + "stop"); this panel
   is the steering wheel — centred at the bottom, wide enough for a full
   sentence, glowing on the subject screen she just opened. Both stay up: the
   walk owns the tabs; Copilot stays open so typing "stop" is one word away.

   Reading mode (phase === 'reading') expands a "what's of concern" card and
   waits until the owner presses Carry on / Next / Back — no countdown that
   moves the screen for them.
   ========================================================================== */

const EYEBROW: Record<TakeoverState['phase'], string> = {
  choosing: 'HOW SHOULD I WORK THIS?',
  driving: 'OPENING — ONE MOMENT',
  waiting: 'WAITING ON YOU — PRESS NEXT',
  reading: 'TAKE YOUR TIME — I\'LL WAIT',
  paused: 'PAUSED — YOU HAVE THE WHEEL',
  holding: 'WAITING ON YOU — YOUR CALL',
  done: 'ALL DONE — PRESS NEXT WHEN READY',
};

/* The eyebrow belongs to the STATE, not the phase alone.
 *
 * `driving` covers two different moments: the round trip before her first
 * line lands, and every step after it. Both wore "OPENING — ONE MOMENT", so
 * from step one onward the panel read "OPENING — ONE MOMENT · 2/3". She is
 * not opening on step 2 — she is working, and a step there may legitimately
 * pause a beat or two waiting for a card to exist before it glows. A label
 * still saying "one moment" while she is three sentences in is what made a
 * normal pace read as HUNG: the mechanism was fine, the caption was lying
 * (bug 59, "hung on OPENING — ONE MOMENT · 2/3 for ~10s"; the chip-tap half
 * of that bug was fixed 08-03 by parallelising the fetch).
 *
 * "ZANA HAS THE WHEEL" is deliberately the mirror of the paused eyebrow —
 * PAUSED · YOU HAVE THE WHEEL — so the two states read as one handover. */
function eyebrowFor(s: TakeoverState): string {
  if (s.agentMode === 'agent' && s.taskTitle) {
    if (s.agentStatus === 'waiting_you') return 'WAITING ON YOU';
    if (s.agentStatus === 'done') return 'DONE';
    if (s.agentStatus === 'queued') return 'QUEUED';
    return 'WORKING';
  }
  if (s.phase === 'driving' && s.step > 0) return 'ZANA HAS THE WHEEL';
  return EYEBROW[s.phase];
}

/** The mascot face. `thinking` is the paused/reading face and is not a script face. */
function faceFor(s: TakeoverState): ZanaState {
  if (s.phase === 'done') return 'happy';
  if (s.phase === 'paused' || s.phase === 'reading' || s.phase === 'waiting') return 'thinking';
  return s.face as ZanaState;
}

export default function ZanaTakeoverPanel({
  state,
  controller,
}: {
  state: TakeoverState | null;
  controller: TakeoverController | null;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  /** Where the owner dragged the panel to, kept across re-renders and runs. */
  const posRef = useRef<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{ dx: number; dy: number; w: number } | null>(null);

  // Re-apply a dragged position after any re-render, and keep the panel on
  // screen if the window was resized while she was away.
  const attach = useCallback((el: HTMLDivElement | null) => {
    panelRef.current = el;
    const pos = posRef.current;
    if (!el || !pos) return;
    el.style.left = `${pos.x}px`;
    el.style.top = `${pos.y}px`;
    el.style.bottom = 'auto';
    el.style.transform = 'none';
  }, []);

  const onGrab = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    const el = panelRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - r.left, dy: e.clientY - r.top, w: r.width };

    const move = (ev: MouseEvent) => {
      const d = dragRef.current;
      if (!d || !panelRef.current) return;
      const x = Math.min(Math.max(8, ev.clientX - d.dx), window.innerWidth - d.w - 8);
      const y = Math.min(Math.max(8, ev.clientY - d.dy), window.innerHeight - 72);
      posRef.current = { x, y };
      panelRef.current.style.left = `${x}px`;
      panelRef.current.style.top = `${y}px`;
      panelRef.current.style.bottom = 'auto';
      panelRef.current.style.transform = 'none';
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      // Let the drag settle before the runtime's own mousemove watcher counts
      // this as "a human took the wheel" — dragging her window is not that.
      window.setTimeout(() => { dragRef.current = null; }, 60);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    e.preventDefault();
  }, []);

  /* Escape always stops. Someone who wants out should not have to find a button.
     Bound once, reading the live controller through a ref, and deliberately NOT
     `[state, controller]`.

     Keydown is a discrete-priority event, so React flushes the resulting state
     update synchronously, inside the dispatch. A listener re-subscribed by a
     state-dependent effect therefore gets removed part-way through the very
     event it is meant to handle — and a removed listener is skipped, so Escape
     did nothing at all here. Mount-once is what makes it reliable. */
  const live = useRef<{ state: TakeoverState | null; controller: TakeoverController | null }>({
    state: null, controller: null,
  });
  live.current = { state, controller };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const { state: s, controller: c } = live.current;
      if (s && c) c.stop();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!state || !controller) return null;

  const { phase, leaving } = state;
  const showChoice = phase === 'choosing';
  const showRead = phase === 'reading' && !!state.read;

  /* An irreversible write must name its victim, or it does not get a button.
   *
   * On 2026-08-11 this panel offered "⚡ Yes — suspend driver" against an
   * account nobody had named, reached from a question about a CAR. The engine
   * side of that is fixed (a pronoun no longer resolves to an account), but a
   * confirm screen is the last thing standing between a person and being
   * logged out of their job, and it should not depend on an upstream fix
   * holding. If the target is missing here, the answer is to ask — never to
   * render a one-tap lockout with a blank where the name goes. */
  const IRREVERSIBLE_NEEDS_TARGET: Record<string, string[]> = {
    suspend_driver: ['driverLabel', 'accountQuery', 'driverId'],
    suspend_bus: ['busId'],
    delete_bus: ['busId'],
  };
  const confirmIntent = state.confirm
    ? { actionId: state.confirm.actionId, params: state.confirm.params || {} }
    : null;
  const requiredTarget = confirmIntent ? IRREVERSIBLE_NEEDS_TARGET[confirmIntent.actionId] : undefined;
  const targetIsNamed = !requiredTarget
    || requiredTarget.some((k) => String((confirmIntent!.params as Record<string, string>)[k] || '').trim());
  const showConfirm = phase === 'holding' && !!state.confirm && targetIsNamed;
  const showUnnamedTargetStop = phase === 'holding' && !!state.confirm && !targetIsNamed;

  // Hold the console still only while she is settling a line or asking for a
  // write. Waiting / reading / paused / done let the owner poke the glow.
  const guardBlocks = phase === 'driving' || phase === 'holding';

  return (
    <>
      <style>{`
        @keyframes ztPanelIn{from{opacity:0;transform:translate(-50%,36px) scale(.94)}to{opacity:1;transform:translate(-50%,0) scale(1)}}
        @keyframes ztPanelOut{from{opacity:1}to{opacity:0;transform:translate(-50%,26px) scale(.96)}}
        @keyframes ztPulse{0%,100%{opacity:.55}50%{opacity:1}}
        @media (prefers-reduced-motion: reduce){
          .zt-panel{animation:none !important}
          .zt-live{animation:none !important}
        }
      `}</style>

      {/* Catches a click anywhere on the page: any sign of a human at the wheel
          pauses her. It also holds the console still while she drives, so the
          owner and the script cannot fight over the same screen.
          Once she is paused with no countdown — or in reading mode — it stops
          intercepting, so they can use the thing she just highlighted. */}
      <div
        onMouseDown={() => controller.nudge()}
        style={{
          position: 'fixed', inset: 0, zIndex: 5900, background: 'transparent',
          pointerEvents: guardBlocks ? 'auto' : 'none',
        }}
      />

      <div
        ref={attach}
        className="zt-panel"
        role="status"
        aria-live="polite"
        style={{
          position: 'fixed',
          left: '50%',
          bottom: showRead ? 'max(28px, 12vh)' : 28,
          transform: 'translateX(-50%)',
          zIndex: 6600,
          width: 'min(580px, 94vw)',
          borderRadius: 20,
          color: '#fff',
          boxSizing: 'border-box',
          background: 'radial-gradient(130% 140% at 80% -20%,#2f2058 0%,#1a1236 55%,#120d22 100%)',
          border: '1px solid rgba(155,124,255,.4)',
          boxShadow: '0 24px 60px -18px rgba(30,18,60,.85), 0 0 44px rgba(111,79,240,.35)',
          animation: leaving
            ? 'ztPanelOut .58s cubic-bezier(.4,0,.2,1) both'
            : 'ztPanelIn .65s cubic-bezier(.22,1,.36,1) both',
        }}
      >
        {/* header — grab handle */}
        <div
          onMouseDown={onGrab}
          style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '13px 18px',
            cursor: 'grab', userSelect: 'none',
          }}
        >
          <Zana state={faceFor(state)} size={54} animated />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 15, fontWeight: 800 }}>
                {state.agentName ? `Zana · ${state.agentName}` : 'Zana'}
              </span>
              <span style={{
                fontSize: 9, fontWeight: 800, letterSpacing: '.08em', color: '#c4b3ff',
                background: 'rgba(155,124,255,.16)', padding: '2px 7px', borderRadius: 5,
              }}>
                {state.agentMode === 'agent' ? 'AGENT' : state.agentMode === 'walkthrough' ? 'WALKTHROUGH' : 'NAVIGATION MODE'}
              </span>
              <span style={{ fontSize: 9, fontWeight: 700, color: '#8d84b0' }}>drag to move</span>
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 9.5, fontWeight: 800, letterSpacing: '.1em', color: '#c4b3ff', marginTop: 3,
            }}>
              {(phase === 'driving' || phase === 'waiting' || state.agentStatus === 'working') && (
                <span className="zt-live" style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: phase === 'waiting' || state.agentStatus === 'waiting_you' ? '#7ddea2' : '#c4b3ff',
                  animation: 'ztPulse 1.4s ease-in-out infinite',
                }} />
              )}
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {state.taskTitle || eyebrowFor(state)}
              </span>
              {state.step > 0 && phase !== 'done' && (
                <span style={{ color: '#8d84b0' }}>· {state.step}/{state.total}</span>
              )}
            </div>
          </div>
          {/* Banner title for agent; subject title for legacy walks. */}
          {!collapsed && !showRead && (
            <div style={{
              fontSize: 11.5, fontWeight: 700, color: '#a99fce', maxWidth: 178,
              textAlign: 'right', lineHeight: 1.3,
            }}>
              {state.agentName ? eyebrowFor(state) : state.title}
            </div>
          )}
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand Zana' : 'Collapse Zana'}
            style={{
              background: 'transparent', border: '1px solid rgba(255,255,255,.18)',
              color: '#c4b3ff', cursor: 'pointer', fontSize: 13, fontWeight: 800,
              lineHeight: 1, padding: '5px 10px', borderRadius: 8, fontFamily: 'inherit',
            }}
          >
            {collapsed ? '+' : '–'}
          </button>
        </div>

        {/* narration */}
        {!collapsed && (
        <div style={{
          padding: '0 18px', marginTop: -3, fontSize: showRead ? 13.5 : 15.5, lineHeight: 1.55,
          fontWeight: 500, color: '#f3effd',
          textWrap: 'pretty' as React.CSSProperties['textWrap'],
          // Same reason as the dock's bubbles: her lines arrive with real
          // breaks in them and HTML would otherwise flatten every list.
          whiteSpace: 'pre-wrap',
        }}>
          {state.say}
        </div>
        )}

        {/* Reading card — what's of concern + timer (prototype UI) */}
        {showRead && !collapsed && state.read && (
          <div style={{
            margin: '12px 18px 0',
            padding: '14px 14px 12px',
            borderRadius: 14,
            background: 'rgba(155,124,255,.12)',
            border: '1px solid rgba(155,124,255,.35)',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 11, fontWeight: 800, letterSpacing: '.06em',
              color: '#c4b3ff', marginBottom: 10, textTransform: 'uppercase',
            }}>
              <span style={{
                width: 7, height: 7, borderRadius: '50%', background: '#ff6b6b', flex: '0 0 auto',
              }} />
              {state.read.title}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {state.read.lines.map((l) => (
                <div key={l.k} style={{
                  display: 'flex', gap: 10, fontSize: 13, lineHeight: 1.45, color: '#f1ecff',
                }}>
                  <span style={{
                    color: '#9b7cff', fontWeight: 800, fontSize: 10.5,
                    flex: '0 0 auto', fontVariantNumeric: 'tabular-nums',
                  }}>{l.k}</span>
                  <span style={{ minWidth: 0 }}>{l.t}</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginTop: 14 }}>
              <span style={{
                fontSize: 12, fontWeight: 700, color: '#a99fce', lineHeight: 1.4,
              }}>
                Take your time — press <b style={{ color: '#e6dfff' }}>Next</b> or{' '}
                <b style={{ color: '#e6dfff' }}>Carry on</b> when you are ready.
              </span>
            </div>
          </div>
        )}

        {/* how she should work this run — asked before anything moves */}
        {showChoice && !collapsed && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '12px 18px 0' }}>
            <button
              type="button"
              onClick={() => controller.choose(true)}
              style={{
                border: 'none', borderRadius: 999, padding: '10px 18px',
                background: 'linear-gradient(135deg,#9b7cff,#6f4ff0)', color: '#fff',
                fontWeight: 800, fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              ⚡ Just do it
            </button>
            <button
              type="button"
              onClick={() => controller.choose(false)}
              style={{
                border: '1px solid rgba(255,255,255,.28)', borderRadius: 999, padding: '10px 16px',
                background: 'transparent', color: '#e6dfff',
                fontWeight: 700, fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Walk me through it
            </button>
          </div>
        )}

        {/* the write she is asking about */}
        {showConfirm && !collapsed && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '12px 18px 0' }}>
            <button
              type="button"
              onClick={() => controller.answerConfirm(true)}
              style={{
                border: 'none', borderRadius: 999, padding: '10px 18px',
                background: 'linear-gradient(135deg,#9b7cff,#6f4ff0)', color: '#fff',
                fontWeight: 800, fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {/* Pass the PARAMS, not just the actionId. labelForZanaIntent has
                  always been able to say "Suspend Kamau" — it was handed
                  `{ actionId }` alone, so it could only ever say "suspend
                  driver". The name existed in state.confirm.params the whole
                  time and never reached the button the owner taps. */}
              ⚡ Yes — {labelForZanaIntent(confirmIntent!).toLowerCase()}
            </button>
            <button
              type="button"
              onClick={() => controller.answerConfirm(false)}
              style={{
                border: '1px solid rgba(255,255,255,.28)', borderRadius: 999, padding: '10px 16px',
                background: 'transparent', color: '#e6dfff',
                fontWeight: 700, fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              No, leave it
            </button>
          </div>
        )}

        {/* The write named nobody. Say so, and offer only the way out. */}
        {showUnnamedTargetStop && !collapsed && (
          <div style={{ padding: '12px 18px 0' }}>
            <div style={{
              border: '1px solid rgba(255,196,120,.45)', borderRadius: 12,
              background: 'rgba(255,176,84,.10)', padding: '10px 12px',
              color: '#ffdfb8', fontSize: 12.5, lineHeight: 1.5,
            }}
            >
              <strong style={{ color: '#ffc978' }}>I stopped myself.</strong>
              {' '}
              I was about to ask you to confirm this, and I cannot tell you who or what it would
              apply to — so there is nothing here worth tapping. Tell me the name or the plate and
              I will set it up again properly.
            </div>
            <div style={{ display: 'flex', gap: 8, paddingTop: 10 }}>
              <button
                type="button"
                onClick={() => controller.answerConfirm(false)}
                style={{
                  border: '1px solid rgba(255,255,255,.28)', borderRadius: 999, padding: '10px 16px',
                  background: 'transparent', color: '#e6dfff',
                  fontWeight: 700, fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Got it — skip this
              </button>
            </div>
          </div>
        )}

        {/* controls — the way out never moves */}
        {/* Back and Next steer a script that has not started yet, so while the
            question is up only the way out is offered. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '12px 18px 14px', marginTop: 12,
          borderTop: '1px solid rgba(255,255,255,.08)',
        }}>
          {(phase === 'paused' || phase === 'reading') && !collapsed && (
            <button
              type="button"
              onClick={() => controller.carryOn()}
              style={{
                border: 'none', borderRadius: 999, padding: '10px 18px',
                background: 'linear-gradient(135deg,#9b7cff,#6f4ff0)', color: '#fff',
                fontWeight: 800, fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit',
                whiteSpace: 'nowrap',
              }}
            >
              {phase === 'reading' ? 'Carry on now →' : 'Carry on →'}
            </button>
          )}
          {(phase === 'driving' || phase === 'waiting') && !collapsed && (
            <button
              type="button"
              onClick={() => controller.pause()}
              style={{
                border: 'none', borderRadius: 999, padding: '10px 18px',
                background: 'rgba(155,124,255,.25)', color: '#efe9ff',
                fontWeight: 800, fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit',
                whiteSpace: 'nowrap',
              }}
            >
              Pause
            </button>
          )}
          {/* Back / Next stay available on the confirm step so you can revisit
              the summary — only Next is hidden on holding (Yes/No own that).
              They also stay available while COLLAPSED and on the done line:
              every owner-paced surface waits for one of these. */}
          <>
            {phase !== 'done' && phase !== 'choosing' && (
              <button
                type="button"
                onClick={() => controller.back()}
                disabled={state.step <= 1}
                title="Previous line"
                style={{
                  border: '1px solid rgba(255,255,255,.22)', borderRadius: 999, padding: '10px 14px',
                  background: 'transparent', color: '#e6dfff',
                  fontWeight: 700, fontSize: 13.5, fontFamily: 'inherit',
                  cursor: state.step <= 1 ? 'not-allowed' : 'pointer',
                  opacity: state.step <= 1 ? 0.4 : 1, whiteSpace: 'nowrap',
                }}
              >
                ← Back
              </button>
            )}
            {phase !== 'holding' && phase !== 'choosing' && (
              <button
                type="button"
                onClick={() => controller.next()}
                title={phase === 'done' ? 'Dismiss' : 'Next line'}
                disabled={phase === 'driving'}
                style={{
                  border: phase === 'waiting' || phase === 'done'
                    ? 'none'
                    : '1px solid rgba(255,255,255,.22)',
                  borderRadius: 999, padding: '10px 14px',
                  background: phase === 'waiting' || phase === 'done'
                    ? 'linear-gradient(135deg,#9b7cff,#6f4ff0)'
                    : 'transparent',
                  color: '#e6dfff',
                  fontWeight: 800, fontSize: 13.5, fontFamily: 'inherit',
                  cursor: phase === 'driving' ? 'not-allowed' : 'pointer',
                  opacity: phase === 'driving' ? 0.45 : 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {phase === 'done' ? 'Got it →' : 'Next →'}
              </button>
            )}
          </>
          <button
            type="button"
            onClick={() => controller.stop()}
            style={{
              border: '1px solid rgba(255,255,255,.3)', borderRadius: 999, padding: '10px 16px',
              background: 'transparent', color: '#e6dfff',
              fontWeight: 700, fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit',
              whiteSpace: 'nowrap',
            }}
          >
            Stop — I've got it
          </button>
          {phase === 'waiting' && !collapsed && (
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#a99fce', whiteSpace: 'nowrap' }}>
              Next · or type stop in Copilot
            </span>
          )}
          {phase === 'driving' && !collapsed && (
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#a99fce', whiteSpace: 'nowrap' }}>
              opening subject · type stop to leave
            </span>
          )}
        </div>
      </div>
    </>
  );
}
